import React, { useState, useContext, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { Avatar, Menu, MenuItem, Divider, Drawer, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, useMediaQuery, useTheme } from '@mui/material';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import SecurityIcon from '@mui/icons-material/Security';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import UserManagement from './pages/UserManagement';
import './App.css';

// Auth Context
import { AuthProvider, AuthContext } from './context/AuthContext';
import Login from './pages/Login';

// Material UI Components
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  ThemeProvider,
  createTheme,
  CssBaseline,
} from '@mui/material';

// Icons
import ShowChartIcon from '@mui/icons-material/ShowChart';
import DashboardIcon from '@mui/icons-material/Dashboard';
import RecommendIcon from '@mui/icons-material/Recommend';
import EventIcon from '@mui/icons-material/Event';
import HomeIcon from '@mui/icons-material/Home';
import CalculateIcon from '@mui/icons-material/Calculate';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';

// Pages
import FundSelector from './pages/FundSelector';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import PortfolioManager from './pages/PortfolioManager';
import SipReminder from './pages/SipReminder';
import Calculators from './pages/Calculators';
import FinancialPlanner from './pages/FinancialPlanner';

// 🔥 નવો કોડ: દર વખતે પેજ બદલાય ત્યારે સ્ક્રોલને ટોપ પર લઈ જવા માટે
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// --- THEME SETUP ---
const theme = createTheme({
  palette: {
    primary: { main: '#204f4f' },
    secondary: { main: '#c4a161' },
    background: { default: '#ffffff' },
    text: { primary: '#204f4f' }
  },
  typography: {
    fontFamily: '"Lato", "Roboto", "Helvetica", "Arial", sans-serif',
    h5: { fontWeight: 700, letterSpacing: '0.5px' },
    button: { textTransform: 'none', fontWeight: 600, fontSize: '15px' },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.6)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.4)',
          borderRadius: 16,
        }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.3)',
          backdropFilter: 'blur(15px)',
          WebkitBackdropFilter: 'blur(15px)',
          color: '#204f4f',
          border: '1px solid rgba(255, 255, 255, 0.6)',
          marginTop: '10px',
          marginLeft: '10px',
          marginRight: '10px',
          width: 'calc(100% - 20px)',
          borderRadius: '16px',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '6px 16px',
          marginLeft: '8px',
          color: '#204f4f',
          transition: 'all 0.3s ease',
          '&:hover': {
            backgroundColor: 'rgba(32, 79, 79, 0.08)'
          }
        }
      }
    },
    // 🔥 ગ્લોબલ સ્ક્રોલબારને પાતળો કરવા માટેનો કોડ અહીં ઉમેર્યો છે
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          '&::-webkit-scrollbar': {
            width: '4px', // એકદમ પાતળો
            height: '4px'
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.1)',
            borderRadius: '10px'
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: 'rgba(0,0,0,0.2)'
          }
        }
      }
    }
  }
});

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useContext(AuthContext);
  if (loading) return <div>Loading...</div>;
  if (!user) return <Navigate to="/login" />;
  return children;
};

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, loading } = useContext(AuthContext); // loading ઉમેર્યું છે
  const [anchorEl, setAnchorEl] = useState(null);

  const [mobileOpen, setMobileOpen] = useState(false);
  const themeContext = useTheme();
  const isMobile = useMediaQuery(themeContext.breakpoints.down('lg'));

  // 🔥 Splash Screen માટે નવું સ્ટેટ
  const [showSplash, setShowSplash] = useState(true);

  useEffect(() => {
    // ૨.૫ સેકન્ડ પછી સ્પલેશ સ્ક્રીન બંધ થશે
    const timer = setTimeout(() => {
      setShowSplash(false);
    }, 2500);
    return () => clearTimeout(timer);
  }, []);

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMobileMenuClick = (path) => {
    setMobileOpen(false);
    setTimeout(() => {
      navigate(path);
    }, 150);
  };

  // 🚀 Splash Screen View (Only Zoom-in Effect)
  if (showSplash) {
    return (
      <Box
        sx={{
          height: '100vh',
          width: '100vw',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'linear-gradient(180deg, #fbf2dd, #f9f9f7)',
          position: 'fixed',
          top: 0,
          left: 0,
          zIndex: 9999
        }}
      >
        <Box
          component="img"
          src="/MFApptrans.png"
          sx={{
            width: { xs: '180px', md: '250px' },
            height: 'auto',
            animation: 'zoomInEffect 1.8s ease-out forwards', // 1.8s સુધી ધીરેથી ઝૂમ થશે
            '@keyframes zoomInEffect': {
              '0%': {
                transform: 'scale(0.3)',
                opacity: 0
              },
              '100%': {
                transform: 'scale(1.1)', // છેલ્લે આ સાઈઝ પર આવીને અટકી જશે
                opacity: 1
              },
            }
          }}
        />
      </Box>
    );
  }

  if (location.pathname === '/login') return <Routes><Route path="/login" element={<Login />} /></Routes>;
  const currentPath = location.pathname;

  const handleDownloadBackup = async () => {
    handleMenuClose();
    try {
      const response = await fetch('/api/backup/download');
      if (!response.ok) throw new Error('Network response was not ok');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `WealthOS_Backup_${new Date().toISOString().slice(0, 10)}.sql`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error('Backup download error:', error);
      alert('Failed to download backup. Make sure the backend server is running.');
    }
  };

  const getActiveStyle = (path) => {
    const isActive = currentPath === path;
    return {
      color: isActive ? '#c4a161' : '#204f4f',
      bgcolor: isActive ? 'rgba(32, 79, 79, 0.08)' : 'transparent',
      fontWeight: isActive ? 'bold' : '600',
      borderBottom: isActive && !isMobile ? '3px solid #c4a161' : 'none',
      borderLeft: isActive && isMobile ? '4px solid #c4a161' : 'none',
      borderRadius: isActive && !isMobile ? '8px 8px 0px 0px' : '8px',
    };
  };

  const menuItems = [
    { text: 'Home', icon: <HomeIcon />, path: '/' },
    { text: 'Planner', icon: <AccountBalanceWalletIcon />, path: '/planner' },
    { text: 'Fund Selector', icon: <ShowChartIcon />, path: '/fund-selector' },
    { text: 'Calculators', icon: <CalculateIcon />, path: '/calculator' },
    { text: 'Analytics', icon: <DashboardIcon />, path: '/analytics' },
    { text: 'Recommended', icon: <RecommendIcon />, path: '/recommended' },
    { text: 'SIP Reminder', icon: <EventIcon />, path: '/sip-reminder', isSip: true },
  ];

  const drawer = (
    <Box sx={{ textAlign: 'center', p: 2 }}>
      <Typography variant="h6" sx={{ my: 2, color: '#204f4f', fontWeight: 'bold' }}>
        Wealth OS Menu
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>
            <ListItemButton
              onClick={() => handleMobileMenuClick(item.path)}
              sx={{
                ...getActiveStyle(item.path),
                border: item.isSip && currentPath === item.path ? '1.5px solid #c4a161' : (item.isSip ? '1.5px solid #204f4f' : 'none'),
                justifyContent: 'flex-start'
              }}
            >
              <ListItemIcon sx={{ color: currentPath === item.path ? '#c4a161' : '#204f4f', minWidth: '40px' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} primaryTypographyProps={{ fontWeight: currentPath === item.path ? 'bold' : '500' }} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        width: '100%',
        background: 'linear-gradient(180deg, #fbf2dd, #f9f9f7)',
        overflow: 'hidden'
      }}
    >
      <AppBar position="fixed">
        <Toolbar sx={{ height: '70px', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 2, color: '#204f4f' }}
              >
                <MenuIcon />
              </IconButton>
            )}

            <img
              src={process.env.PUBLIC_URL + '/logo512.png'}
              alt="Mahargh Logo"
              style={{ height: isMobile ? '35px' : '45px', width: 'auto', objectFit: 'contain' }}
            />

            <Box
              sx={{
                height: isMobile ? '20px' : '30px',
                width: '1.5px',
                backgroundColor: 'rgba(32, 79, 79, 0.3)',
                marginLeft: isMobile ? '8px' : '15px',
                marginRight: isMobile ? '8px' : '15px'
              }}
            />

            <Typography
              variant="h6"
              component="div"
              sx={{
                fontFamily: '"Optima", "Trebuchet MS", sans-serif',
                fontWeight: 700,
                letterSpacing: '0.5px',
                color: '#204f4f',
                fontSize: isMobile ? '1.1rem' : '1.3rem'
              }}
            >
              Wealth OS
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
                {menuItems.map((item) => (
                  <Button
                    key={item.text}
                    component={Link}
                    to={item.path}
                    startIcon={item.icon}
                    sx={{
                      ...getActiveStyle(item.path),
                      ...(item.isSip && {
                        border: currentPath === item.path ? '1.5px solid #c4a161' : '1.5px solid #204f4f',
                        borderBottom: currentPath === item.path ? '3px solid #c4a161' : '1.5px solid #204f4f',
                        borderRadius: '8px',
                        ml: 2,
                        '&:hover': {
                          background: 'rgba(32, 79, 79, 0.1)',
                          border: currentPath === item.path ? '1.5px solid #c4a161' : '1.5px solid #204f4f',
                        }
                      })
                    }}
                  >
                    {item.text}
                  </Button>
                ))}
              </Box>
            )}

            {user && (
              <>
                {!isMobile && <Divider orientation="vertical" flexItem sx={{ mx: 2, my: 1.5, borderColor: '#c4a161', borderRightWidth: 2 }} />}
                <Box display="flex" alignItems="center" gap={1} onClick={handleMenuOpen} sx={{ cursor: 'pointer', p: 0.5, borderRadius: 2, '&:hover': { bgcolor: '#f4fbf7' } }}>
                  <Avatar sx={{ bgcolor: '#204f4f', width: 36, height: 36, fontSize: '16px', fontWeight: 'bold' }}>
                    {user?.name ? user.name.charAt(0) : 'U'}
                  </Avatar>
                  {!isMobile && (
                    <Box display={{ xs: 'none', lg: 'block' }}>
                      <Typography variant="body2" fontWeight="bold" color="#204f4f" lineHeight={1.1}>{user?.name || 'User'}</Typography>
                      <Typography variant="caption" color="#c4a161" fontWeight="bold">{user.role}</Typography>
                    </Box>
                  )}
                </Box>
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} PaperProps={{ sx: { mt: 1.5, width: 220, borderRadius: 2, bgcolor: '#ffffff', boxShadow: '0 8px 24px rgba(0,0,0,0.1)' } }}>
                  {(user.role === 'System Owner' || user.role === 'Administrator') && (
                    <MenuItem onClick={() => { handleMenuClose(); navigate('/user-management'); }}>
                      <ManageAccountsIcon sx={{ mr: 2, color: '#204f4f' }} fontSize="small" /> User Management
                    </MenuItem>
                  )}
                  {(user.role === 'System Owner' || user?.permissions?.systemBackup) && (
                    <MenuItem onClick={handleDownloadBackup}>
                      <CloudDownloadIcon sx={{ mr: 2, color: '#204f4f' }} fontSize="small" /> Download Backup
                    </MenuItem>
                  )}
                  <Divider />
                  <MenuItem onClick={() => { handleMenuClose(); logout(); }}>
                    <LogoutIcon sx={{ mr: 2, color: '#555' }} fontSize="small" /> Logout
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      <nav>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', lg: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 260, backgroundColor: '#fbf2dd' },
          }}
        >
          {drawer}
        </Drawer>
      </nav>

      <Box
        sx={{
          flexGrow: 1,
          p: { xs: 1, md: 2 },
          pt: { xs: '90px', md: '100px' },
          width: '100%',
          overflow: 'hidden',
          boxSizing: 'border-box'
        }}
      >
        <Routes>
          <Route path="/" element={
            <ProtectedRoute>
              <Box textAlign="center" mt={{ xs: 4, md: 6 }}>
                <Typography variant={isMobile ? "h4" : "h3"} gutterBottom sx={{ color: '#204f4f', fontWeight: '900', textShadow: '0 2px 4px rgba(0,0,0,0.05)', px: 2 }}>
                  Welcome to Mahargh Nivesh WealthOS
                </Typography>
                <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ color: '#546e7a', fontWeight: 500, mb: 4 }}>
                  Simplifying Mutual Fund Analysis
                </Typography>

                <Box
                  component="img"
                  src={process.env.PUBLIC_URL + '/Home.png'}
                  alt="Mahargh WealthOS Dashboard"
                  sx={{
                    width: '100%',
                    maxWidth: '850px',
                    maxHeight: { xs: '40vh', md: '65vh' },
                    objectFit: 'contain',
                    background: 'transparent',
                    display: 'block',
                    margin: '0 auto',
                    filter: 'drop-shadow(0px 12px 20px rgba(32, 79, 79, 0.15))'
                  }}
                  onError={(e) => { e.target.style.display = 'none' }}
                />
              </Box>
            </ProtectedRoute>
          } />
          <Route path="/fund-selector" element={<ProtectedRoute><FundSelector /></ProtectedRoute>} />
          <Route path="/calculator" element={<ProtectedRoute><Calculators /></ProtectedRoute>} />
          <Route path="/analytics" element={<ProtectedRoute><AnalyticsDashboard /></ProtectedRoute>} />
          <Route path="/recommended" element={<ProtectedRoute><PortfolioManager /></ProtectedRoute>} />
          <Route path="/sip-reminder" element={<ProtectedRoute><SipReminder /></ProtectedRoute>} />
          <Route path="/planner" element={<ProtectedRoute><FinancialPlanner /></ProtectedRoute>} />
          <Route path="/user-management" element={<ProtectedRoute><UserManagement /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Box>
    </Box>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <AppContent />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;